import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  TrendingUp, DollarSign, BarChart2, Activity,
  Calendar, SlidersHorizontal, ChevronDown, ChevronUp,
  Search, Bell, User, Plus, X, Wallet, Eye, EyeOff,
  Clock, ArrowUpRight, ArrowDownRight, Layers, Tag, Landmark, ArrowUp, ArrowDown, CreditCard
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import KPICard from '../components/KPICard';
import QuickStatCard from '../components/QuickStatCard';
import StatusBadge from '../components/StatusBadge';
import LancamentoModal from '../components/LancamentoModal';
import HeaderBar from '../components/HeaderBar';
import api from '../utils/api';
import { resolveDashboardCards, isMenuItem } from '../utils/dashboardCards';
import { formatCurrency, formatDate, formatDateChip } from '../utils/format';

// Paleta de cores semânticas inspirada na marca Gastify
const DONUT_COLORS_MAP = {
  Receitas: '#10B981', // Verde Esmeralda (Entradas)
  Custos: '#F59E0B',   // Âmbar / Laranja (Custos Operacionais)
  Despesas: '#EF4444', // Vermelho Coral (Despesas)
  RECEITA: '#10B981',
  CUSTO: '#F59E0B',
  DESPESA: '#EF4444',
};

const getDonutColor = (name, index) => {
  if (name && DONUT_COLORS_MAP[name]) return DONUT_COLORS_MAP[name];
  const fallback = ['#10B981', '#F59E0B', '#EF4444', '#0EA5E9', '#8B5CF6', '#14B8A6'];
  return fallback[index % fallback.length];
};

const CustomAreaTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const val = payload[0].value;
  return (
    <div className="bg-slate-900/90 backdrop-blur-md text-white rounded-xl px-3 py-1.5 shadow-xl text-xs font-semibold flex items-center gap-1.5 border border-white/20">
      <span className="w-2 h-2 rounded-full bg-[#10B981]" />
      <span>{label}:</span>
      <span className="text-[#86EFAC]">{formatCurrency(val)}</span>
    </div>
  );
};

// Componente para desenhar tooltips flutuantes nos pontos como na imagem
const CustomizedDot = (props) => {
  const { cx, cy, payload, value, index } = props;
  if (cx === undefined || cy === undefined || isNaN(cx) || isNaN(cy)) return null;
  
  const num = typeof value === 'number' ? value : parseFloat(value) || 0;
  // Exibir pills apenas em pontos selecionados para simular a imagem de referência
  const showPill = index === 1 || index === 2 || index === 3 || index === 4 || index === 5;
  const formattedVal = num >= 1000 ? `R$ ${(num / 1000).toFixed(1)}K` : `R$ ${num.toFixed(0)}`;

  return (
    <g key={index}>
      <circle cx={cx} cy={cy} r={4} fill="#FFFFFF" stroke="#0D9488" strokeWidth={2.5} />
      {showPill && (
        <g transform={`translate(${cx - 28}, ${cy - 24})`}>
          <rect
            width={56}
            height={18}
            rx={9}
            fill="#FFFFFF"
            stroke="#E2E8F0"
            strokeWidth={1}
            filter="drop-shadow(0px 2px 4px rgba(0,0,0,0.06))"
          />
          <text
            x={28}
            y={12}
            textAnchor="middle"
            fill="#334155"
            fontSize={9}
            fontWeight="bold"
            fontFamily="Plus Jakarta Sans, sans-serif"
          >
            {formattedVal}
          </text>
        </g>
      )}
    </g>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const cfg = resolveDashboardCards(user);

  const [indicadores, setIndicadores] = useState(null);
  const [kpiSeries, setKpiSeries] = useState([]);
  const [lancamentos, setLancamentos] = useState([]);
  const [historicoMensal, setHistoricoMensal] = useState([]);
  const [fatCategoria, setFatCategoria] = useState([]);
  const [distribuicaoTipo, setDistribuicaoTipo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ data_inicio: '', data_fim: '' });
  const [showModal, setShowModal] = useState(false);
  const [carteiras, setCarteiras] = useState([]);
  const [cartoes, setCartoes] = useState([]);
  const [carteiraFiltro, setCarteiraFiltro] = useState('');
  const [showSaldo, setShowSaldo] = useState(true);
  const [chartMode, setChartMode] = useState('Receitas');
  const [searchQuery, setSearchQuery] = useState('');

  // Carregar carteiras com saldos
  useEffect(() => {
    api.get('/carteiras/saldos').then(r => setCarteiras(r.data)).catch(() => {
      api.get('/carteiras').then(r => setCarteiras(r.data)).catch(() => {});
    });
  }, []);

  useEffect(() => {
    api.get('/cartoes').then(r => setCartoes(r.data || [])).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
      if (filters.data_fim) params.append('data_fim', filters.data_fim);
      if (carteiraFiltro) params.append('carteira_id', carteiraFiltro);
      const qs = params.toString();
      const [indRes, lancRes, histRes, catRes, distRes, seriesRes] = await Promise.all([
        api.get(`/dashboard/indicadores${qs ? '?' + qs : ''}`),
        api.get(`/lancamentos${qs ? '?' + qs : ''}`),
        api.get('/dashboard/faturamento-historico'),
        api.get('/dashboard/faturamento-categoria'),
        api.get(`/dashboard/distribuicao-tipo${qs ? '?' + qs : ''}`),
        api.get(`/dashboard/kpi-series${carteiraFiltro ? `?carteira_id=${carteiraFiltro}` : ''}`),
      ]);
      setIndicadores(indRes.data);
      setKpiSeries(seriesRes.data || []);
      setLancamentos(lancRes.data || []);
      setHistoricoMensal(histRes.data || []);
      setFatCategoria(catRes.data || []);
      setDistribuicaoTipo(distRes.data || []);
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, carteiraFiltro]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const ind = indicadores || {};

  // Com uma conta selecionada, só entram os cartões cuja fatura é debitada nela
  const cartoesVisiveis = carteiraFiltro ? cartoes.filter(c => c.carteiraDebitoId === carteiraFiltro) : cartoes;

  // Séries mensais reais e variação do mês atual vs. o anterior.
  const serieDe = (key) => kpiSeries.map(s =>
    key === 'margem_bruta' && !(s.faturamento_bruto > 0) ? null : s[key]
  );
  const variacao = (serie, emPontos = false) => {
    if (serie.length < 2) return null;
    const cur = serie[serie.length - 1];
    const prev = serie[serie.length - 2];
    if (cur == null || prev == null) return null;
    const delta = emPontos ? cur - prev : (prev === 0 ? null : ((cur - prev) / Math.abs(prev)) * 100);
    if (delta == null || !Number.isFinite(delta)) return null;
    const abs = Math.abs(delta);
    const txt = `${delta >= 0 ? '+' : '-'}${abs >= 10 ? abs.toFixed(0) : abs.toFixed(1)}${emPontos ? ' p.p.' : '%'}`;
    return { txt, up: delta >= 0 };
  };
  const kpiTrend = (key, emPontos) => {
    const serie = serieDe(key);
    return { series: serie, trend: variacao(serie, emPontos) };
  };
  const tFat = kpiTrend('faturamento_bruto');
  const tLucro = kpiTrend('lucro_bruto');
  const tMargem = kpiTrend('margem_bruta', true);
  const tEbitda = kpiTrend('ebitda');

  // Sem dados de exemplo: tenant novo começa zerado e os gráficos mostram um aviso
  const growthData = historicoMensal.map(h => ({
    month: h.mes,
    value: chartMode === 'Receitas' ? (h.receitas || 0) : chartMode === 'Custos' ? (h.custos || 0) : (h.despesas || 0),
  }));
  const growthVazio = growthData.every(d => !d.value);

  // Dados do Donut Chart de distribuição
  const donutData = distribuicaoTipo
    .map(d => ({
      name: d.tipo === 'RECEITA' ? 'Receitas' : d.tipo === 'CUSTO' ? 'Custos' : d.tipo === 'DESPESA' ? 'Despesas' : d.tipo,
      value: d.total || 0,
    }))
    .filter(d => d.value > 0);

  // Carteira ativa saldo
  const carteiraAtivaSaldo = carteiraFiltro
    ? (carteiras.find(c => c.id === carteiraFiltro)?.saldo ?? null)
    : carteiras.reduce((sum, c) => sum + (c.saldo ?? 0), 0);

  // 5 lançamentos mais recentes para a lista rápida
  const filteredLancamentos = lancamentos.slice(0, 5);

  return (
    <div className="space-y-6 page-enter pb-10" data-testid="dashboard-page">
      {/* Top Header Bar Funcional (Pesquisa, Notificações Changelog e Perfil Admin) */}
      <HeaderBar
        title="Dashboard"
        onNovoLancamento={() => setShowModal(true)}
      />

      {/* Seletor rápido de carteiras / contas em pílulas */}
      {cfg.filtro_contas && carteiras.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mr-1">Filtrar por Conta:</span>
          <button
            onClick={() => setCarteiraFiltro('')}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all shadow-xs ${
              !carteiraFiltro
                ? 'bg-slate-900 text-white shadow-sm'
                : 'bg-white text-slate-600 border border-slate-200/80 hover:bg-slate-50 hover:border-slate-300'
            }`}
          >
            Todas as Contas
          </button>
          {carteiras.map(c => {
            const saldoVal = c.saldo ?? 0;
            const isNegative = saldoVal < 0;
            return (
              <button
                key={c.id}
                onClick={() => setCarteiraFiltro(carteiraFiltro === c.id ? '' : c.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all flex items-center gap-1.5 shadow-xs ${
                  carteiraFiltro === c.id
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-white text-slate-700 border border-slate-200/80 hover:bg-slate-50 hover:border-slate-300'
                }`}
              >
                <span className="w-2 h-2 rounded-full" style={{ background: c.cor || (isNegative ? '#EF4444' : '#10B981') }} />
                <span>{c.nome}</span>
                {showSaldo && (
                  <span className={`text-[11px] font-semibold opacity-80 ${carteiraFiltro === c.id ? 'text-slate-200' : isNegative ? 'text-rose-600' : 'text-slate-500'}`}>
                    ({formatCurrency(saldoVal)})
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Atalhos rápidos: Saldo atual, Receitas, Despesas e Cartão de crédito */}
      {(cfg.saldo || cfg.receitas || cfg.despesas || cfg.cartao) && (
        <div className="space-y-2">
          {cfg.saldo && (
            <div className="flex justify-end">
              <button
                onClick={() => setShowSaldo(v => !v)}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                title={showSaldo ? 'Ocultar saldo' : 'Exibir saldo'}
              >
                {showSaldo ? <Eye size={13} /> : <EyeOff size={13} />}
                <span>{showSaldo ? 'Ocultar saldo' : 'Exibir saldo'}</span>
              </button>
            </div>
          )}
          <div
            className="grid gap-3 sm:gap-4"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}
            data-testid="quick-stats-row"
          >
            {cfg.saldo && (
              <QuickStatCard
                testId="quick-card-saldo"
                label={carteiraFiltro ? `Saldo · ${carteiras.find(c => c.id === carteiraFiltro)?.nome || 'Conta'}` : 'Saldo atual'}
                value={showSaldo ? formatCurrency(carteiraAtivaSaldo ?? 0) : '••••••'}
                icon={Landmark}
                color="#6366F1"
                loading={loading}
                onClick={() => navigate(`/${tenantSlug}/carteira`)}
              />
            )}
            {cfg.receitas && (
              <QuickStatCard
                testId="quick-card-receitas"
                label="Receitas"
                value={formatCurrency(ind.faturamento_bruto || 0)}
                footer={`A receber: ${formatCurrency(ind.a_receber || 0)}`}
                icon={ArrowUp}
                color="#10B981"
                loading={loading}
                onClick={() => navigate(`/${tenantSlug}/lancamentos?tipo=RECEITA`)}
              />
            )}
            {cfg.despesas && (
              <QuickStatCard
                testId="quick-card-despesas"
                label="Despesas"
                value={formatCurrency(ind.total_despesas || 0)}
                footer={`A pagar: ${formatCurrency(ind.a_pagar || 0)}`}
                icon={ArrowDown}
                color="#EF4444"
                loading={loading}
                onClick={() => navigate(`/${tenantSlug}/lancamentos?tipo=DESPESA`)}
              />
            )}
            {cfg.cartao && (
              <QuickStatCard
                testId="quick-card-cartao"
                label="Cartão de crédito"
                value={formatCurrency(cartoesVisiveis.reduce((s, c) => s + (c.limiteUsado || 0), 0))}
                footer={cartoes.length === 0 ? 'Nenhum cartão cadastrado' : (carteiraFiltro && cartoesVisiveis.length === 0 ? 'Nenhum cartão ligado a esta conta' : 'Faturas em aberto')}
                icon={CreditCard}
                color="#F59E0B"
                onClick={() => navigate(`/${tenantSlug}/cartoes`)}
              />
            )}
          </div>
        </div>
      )}

      {/* Indicadores empresariais (opcionais, ligados em Configurações > Dashboard) */}
      {(cfg.faturamento_bruto || cfg.lucro_bruto || cfg.margem_bruta || cfg.ebitda || cfg.custos || cfg.despesas_fixas || cfg.despesas_variaveis || cfg.despesas_nao_op) && (
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}
          data-testid="kpi-row-empresarial"
        >
          {cfg.faturamento_bruto && (
            <KPICard label="Faturamento Bruto" value={ind.faturamento_bruto || 0}
              series={tFat.series} trend={tFat.trend?.txt} trendPositive={tFat.trend?.up}
              color="#10B981" loading={loading} />
          )}
          {cfg.lucro_bruto && (
            <KPICard label="Lucro Bruto" value={ind.lucro_bruto || 0}
              series={tLucro.series} trend={tLucro.trend?.txt} trendPositive={tLucro.trend?.up}
              color="#0EA5E9" loading={loading} />
          )}
          {cfg.margem_bruta && (
            <KPICard label="Margem Bruta" value={ind.margem_bruta || 0} isPercent={true}
              series={tMargem.series} trend={tMargem.trend?.txt} trendPositive={tMargem.trend?.up}
              color="#6366F1" loading={loading} />
          )}
          {cfg.ebitda && (
            <KPICard label="EBITDA" value={ind.ebitda || 0}
              series={tEbitda.series} trend={tEbitda.trend?.txt} trendPositive={tEbitda.trend?.up}
              color="#14B8A6" loading={loading} />
          )}
          {cfg.custos && (
            <KPICard label="Custos Totais" value={ind.total_custos || 0} color="#F59E0B" loading={loading} />
          )}
          {cfg.despesas_fixas && (
            <KPICard label="Despesas Fixas" value={ind.despesas_fixas || 0} color="#EF4444" loading={loading} />
          )}
          {cfg.despesas_variaveis && (
            <KPICard label="Despesas Variáveis" value={ind.despesas_variaveis || 0} color="#EF4444" loading={loading} />
          )}
          {cfg.despesas_nao_op && (
            <KPICard label="Despesas Não Operacionais" value={ind.despesas_nao_operacionais || 0} color="#EF4444" loading={loading} />
          )}
        </div>
      )}

      {!Object.entries(cfg).some(([id, on]) => on && !isMenuItem(id)) && (
        <div className="fin-card p-8 text-center text-sm text-slate-500 dark:text-slate-400">
          Nenhum card ativo. Escolha o que mostrar em{' '}
          <button className="font-bold text-indigo-500 hover:underline" onClick={() => navigate(`/${tenantSlug}/configuracoes`)}>
            Configurações › Dashboard
          </button>.
        </div>
      )}

      {/* Middle Big Chart Card: "Evolução do Faturamento" */}
      {cfg.evolucao && (
      <div className="bg-white rounded-[24px] p-6 shadow-[0_14px_34px_-10px_rgba(112,144,176,0.15)] border border-white/90" data-testid="revenue-growth-card">
        <div className="flex items-center justify-between mb-6">
          <h2
            className="text-[16px] font-bold text-slate-800 tracking-tight"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            Crescimento Financeiro ao Longo do Tempo
          </h2>

          <div className="relative">
            <select
              value={chartMode}
              onChange={e => setChartMode(e.target.value)}
              className="appearance-none bg-[#F4F7FA] hover:bg-slate-100 text-slate-600 text-xs font-semibold px-3.5 py-1.5 pr-7 rounded-xl border border-slate-200/60 focus:outline-none cursor-pointer transition-colors"
            >
              <option value="Receitas">Gráfico Interativo ˅</option>
              <option value="Custos">Custos Totais</option>
              <option value="Despesas">Despesas Fixas</option>
            </select>
            <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        <div className="h-[260px] w-full">
          {growthVazio ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-400" data-testid="growth-empty">Sem dados ainda. O gráfico aparece com os seus primeiros lançamentos.</div>
          ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={growthData} margin={{ top: 30, right: 20, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0D9488" stopOpacity={0.35} />
                  <stop offset="60%" stopColor="#10B981" stopOpacity={0.12} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0.0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: '#94A3B8', fontWeight: 500 }}
                dy={8}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: '#94A3B8', fontWeight: 500 }}
                tickFormatter={(val) => `R$ ${val >= 1000 ? `${(val / 1000).toFixed(0)}K` : val}`}
              />
              <Tooltip content={<CustomAreaTooltip />} />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#0D9488"
                strokeWidth={2.5}
                fill="url(#areaGradient)"
                dot={<CustomizedDot />}
                activeDot={{ r: 6, fill: '#0D9488', stroke: '#FFFFFF', strokeWidth: 3 }}
              />
            </AreaChart>
          </ResponsiveContainer>
          )}
        </div>
      </div>
      )}

      {/* Bottom Row: Recent Project Activity + Task Distribution */}
      {(cfg.recentes || cfg.distribuicao) && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Cols: Lançamentos Recentes */}
        {cfg.recentes && (
        <div className={`${cfg.distribuicao ? 'lg:col-span-2' : 'lg:col-span-3'} bg-white rounded-[24px] p-6 shadow-[0_14px_34px_-10px_rgba(112,144,176,0.15)] border border-white/90 flex flex-col justify-between`} data-testid="recent-activity-card">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2
                className="text-[16px] font-bold text-slate-800 tracking-tight"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                Lançamentos e Atividades Recentes
              </h2>

              <div className="relative">
                <button
                  onClick={() => setShowFilters(v => !v)}
                  className="flex items-center gap-1.5 bg-[#F4F7FA] hover:bg-slate-100 text-slate-600 text-xs font-semibold px-3 py-1.5 rounded-xl border border-slate-200/60 transition-colors"
                >
                  <span>Todas as Carteiras</span>
                  <ChevronDown size={12} className="text-slate-400" />
                </button>
              </div>
            </div>

            {/* Sub-header Table Columns */}
            <div className="grid grid-cols-12 text-[11px] font-semibold text-slate-400 uppercase tracking-wider py-2 border-b border-slate-100">
              <div className="col-span-5 sm:col-span-4">Lançamento</div>
              <div className="col-span-2 sm:col-span-2 text-center">Origem</div>
              <div className="col-span-3 sm:col-span-3 text-center">Status</div>
              <div className="col-span-2 sm:col-span-3 text-right">Progresso / Valor</div>
            </div>

            {/* Table Rows */}
            <div className="divide-y divide-slate-50">
              {loading ? (
                Array(4).fill(0).map((_, i) => (
                  <div key={i} className="py-3.5 flex items-center gap-4 animate-pulse">
                    <div className="flex-1 h-4 bg-slate-100 rounded" />
                    <div className="w-16 h-4 bg-slate-100 rounded" />
                  </div>
                ))
              ) : filteredLancamentos.length === 0 ? (
                <div className="py-10 text-center" data-testid="recent-activity-empty">
                  <p className="text-sm font-semibold text-slate-500">Nenhum lançamento ainda</p>
                  <p className="text-xs text-slate-400 mt-1">Seus lançamentos mais recentes vão aparecer aqui.</p>
                </div>
              ) : (
                filteredLancamentos.map(l => (
                  <div key={l.id} className="grid grid-cols-12 items-center py-3.5 hover:bg-slate-50/60 transition-colors">
                    <div className="col-span-5 sm:col-span-4 min-w-0 pr-2">
                      <p className="text-xs font-semibold text-slate-800 truncate">{l.descricao}</p>
                      <p className="text-[10px] text-slate-400 truncate">{l.categoria || 'Geral'}</p>
                    </div>
                    {/* Team Avatar */}
                    <div className="col-span-2 sm:col-span-2 flex items-center justify-center -space-x-1.5">
                      <div className="w-5 h-5 rounded-full bg-slate-200 ring-2 ring-white text-[9px] font-bold flex items-center justify-center text-slate-600">
                        {l.carteiraNome?.[0] || 'C'}
                      </div>
                    </div>
                    {/* Status */}
                    <div className="col-span-3 sm:col-span-3 flex justify-center">
                      <StatusBadge type="status" value={l.status || 'pago'} />
                    </div>
                    {/* Progress / Value */}
                    <div className="col-span-2 sm:col-span-3 flex items-center justify-end gap-2">
                      <div className="hidden sm:block w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${Math.min(100, Math.max(20, (l.valor / 100)))}%`,
                            background: l.tipo === 'RECEITA' ? '#10B981' : l.tipo === 'CUSTO' ? '#F59E0B' : '#EF4444'
                          }}
                        />
                      </div>
                      <span className="text-xs font-bold text-slate-800">{formatCurrency(l.valor)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="pt-3 border-t border-slate-50 flex justify-end">
            <button
              onClick={() => navigate(`/${tenantSlug}/lancamentos`)}
              className="text-xs font-bold text-[#4A6FD8] hover:text-[#3B59B3] transition-colors"
            >
              Ver todos os lançamentos →
            </button>
          </div>
        </div>

        )}

        {/* Right Col: Distribuição Financeira (Donut Chart) */}
        {cfg.distribuicao && (
        <div className={`${cfg.recentes ? '' : 'lg:col-span-3'} bg-white rounded-[24px] p-6 shadow-[0_14px_34px_-10px_rgba(112,144,176,0.15)] border border-white/90 flex flex-col justify-between`} data-testid="task-distribution-card">
          <div>
            <h2
              className="text-[16px] font-bold text-slate-800 tracking-tight mb-4"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              Distribuição Financeira
            </h2>

            {/* Donut Chart */}
            <div className="h-[180px] w-full flex items-center justify-center">
              {donutData.length === 0 ? (
                <p className="text-sm text-slate-400 text-center px-4" data-testid="donut-empty">Sem dados ainda.</p>
              ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={75}
                    paddingAngle={3}
                    dataKey="value"
                    stroke="#FFFFFF"
                    strokeWidth={2}
                  >
                    {donutData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getDonutColor(entry.name, index)} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [formatCurrency(value), 'Total']} />
                </PieChart>
              </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Legend Dots at bottom */}
          <div className="flex flex-wrap items-center justify-center gap-3 pt-3 border-t border-slate-50 text-[11px] font-semibold text-slate-600">
            {donutData.map((item, idx) => (
              <div key={idx} className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 rounded-full border border-slate-100 shadow-xs">
                <span
                  className="w-2.5 h-2.5 rounded-full shadow-xs"
                  style={{ background: getDonutColor(item.name, idx) }}
                />
                <span>{item.name}</span>
              </div>
            ))}
          </div>
        </div>
        )}
      </div>
      )}

      {/* Modal de Novo Lançamento */}
      <LancamentoModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={fetchData}
      />
    </div>
  );
}
